import { InputBarProvider } from "@app/components/assistant/conversation/input_bar/InputBarContext";
import { WelcomeTourGuideProvider } from "@app/components/assistant/WelcomeTourGuideProvider";
import { DesktopNavigationProvider } from "@app/components/navigation/DesktopNavigationContext";
import { QuickStartGuide } from "@app/components/QuickStartGuide";
import { useDatadogLogs } from "@app/hooks/useDatadogLogs";
import { useSetupNotifications } from "@app/hooks/useSetupNotifications";
import { useAuth } from "@app/lib/auth/AuthContext";
import { ClientTypeProvider } from "@app/lib/context/clientType";
import { Head, Script } from "@app/lib/platform";
import { getFaviconPath } from "@app/lib/utils";
import type React from "react";
import { useEffect } from "react";

export default function AppRootLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useAuth();
  useDatadogLogs();
  useSetupNotifications();
  const faviconPath = getFaviconPath();

  useEffect(() => {
    if (typeof window !== "undefined" && user?.sId) {
      // Identify the user with GTM
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        userId: user.sId,
        event: "userIdentified",
      });
    }
  }, [user?.sId]);

  return (
    <ClientTypeProvider value="web">
      <WelcomeTourGuideProvider>
        <DesktopNavigationProvider>
          <InputBarProvider>
            <Head>
              <link rel="icon" type="image/png" href={faviconPath} />

              <meta name="apple-mobile-web-app-title" content="Dust" />
              <link rel="apple-touch-icon" href="/static/AppIcon.png" />
              <link
                rel="apple-touch-icon"
                sizes="60x60"
                href="/static/AppIcon_60.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="76x76"
                href="/static/AppIcon_76.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="120x120"
                href="/static/AppIcon_120.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="152x152"
                href="/static/AppIcon_152.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="167x167"
                href="/static/AppIcon_167.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="180x180"
                href="/static/AppIcon_180.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="192x192"
                href="/static/AppIcon_192.png"
              />
              <link
                rel="apple-touch-icon"
                sizes="228x228"
                href="/static/AppIcon_228.png"
              />

              <meta
                name="viewport"
                content="width=device-width, initial-scale=1, maximum-scale=1"
              />
            </Head>
            {children}
            <QuickStartGuide />
            <Script id="google-tag-manager" strategy="afterInteractive">
              {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM_TRACKING_ID}');
          `}
            </Script>
          </InputBarProvider>
        </DesktopNavigationProvider>
      </WelcomeTourGuideProvider>
    </ClientTypeProvider>
  );
}
